import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, TextField, Autocomplete, Box, Dropdown, DropdownMenuItem, Button} from 'views/components';

import {TimePicker, DatePicker, DateTimePicker} from 'views/module';

import {translate} from 'views/module';

import moment from 'moment';
import momentTZ from 'moment-timezone';

const Fixed = (props) => {
  const newEvent = props.newEvent;
  const updateEventData = props.updateEventData;
  const setIsStepValid = props.setIsStepValid;

  const eventData = newEvent?.eventData || {};
  const startTime = eventData.startTime || null;
  const endTime = eventData.endTime || null;
  const timezone = eventData.timezone;

  const setStartTime = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        startTime: newValue,
      };
    });
  };
  const setEndTime = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        endTime: newValue,
      };
    });
  };
  const setTimezone = (newValue) => {
    updateEventData((e) => {
      return {
        ...e,
        timezone: newValue,
      };
    });
  };

  // Sets default end time
  React.useEffect(() => {
    if (startTime && !endTime) {
      const startTimeMoment = moment(startTime);
      const endTimeMoment = startTimeMoment.add(2, 'hour');
      setEndTime(endTimeMoment);
    }
    if (endTime) {
      const startTimeMoment = moment(startTime);
      if (startTimeMoment.isAfter(moment(endTime))) {
        const endTimeMoment = startTimeMoment.add(2, 'hour');
        setEndTime(endTimeMoment);
      }
    }
  }, [startTime, endTime]);

  // Steps validation
  React.useEffect(() => {
    if (!(startTime && endTime && timezone)) {
      setIsStepValid(false);
      return;
    }
    if (endTime.isBefore(startTime)) {
      setIsStepValid(false);
      return;
    }
    setIsStepValid(true);
  }, [startTime, endTime, timezone]);

  // Sets defaul timezone
  React.useEffect(() => {
    if (!timezone) {
      setTimezone(momentTZ.tz.guess());
    }
  }, [timezone]);

  const makeTimezonesList = React.useCallback(() => {
    const timezones = momentTZ.tz.names();
    const formatted = timezones.map((timezone) => {
      return {
        group: translate('client.ClientDetail.Scheduling.Timezone.Timezones'),
        value: timezone,
        label: timezone,
      };
    });
    return [...formatted];
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <DateTimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.StartTime')}
          value={startTime}
          onChange={(e) => setStartTime(e)}
        />
      </Grid>
      <Grid item xs={6}>
        <DateTimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.EndTime')}
          value={endTime}
          onChange={setEndTime}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={makeTimezonesList()}
          value={timezone || momentTZ.tz.guess()}
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.FixedEvent.Timezone')}
          onChange={(e) => setTimezone(e.value)}
          sx={{minWidth: {xs: '192px', md: '256px'}}}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            );
          }}
          groupBy={(dropdownItem) => dropdownItem.group}
          isOptionEqualToValue={(option, value) => option.value === value}
          disableClearable
        />
      </Grid>
    </Grid>
  );
};

const frequencies = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
const daysOfWeek = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const Recurrence = (props) => {
  const newEvent = props.newEvent;
  const updateRecurrenceData = props.updateRecurrenceData;
  const setIsStepValid = props.setIsStepValid;

  const recurrenceData = newEvent.recurrenceData || {};
  const startDate = recurrenceData.startDate || null;
  const endDate = recurrenceData.endDate || null;
  const startTime = recurrenceData.startTime || null;
  const endTime = recurrenceData.endTime || null;
  const freq = recurrenceData.freq || 'DAILY';
  const interval = recurrenceData.interval || 1;
  const byweekday = recurrenceData.byweekday || [];
  const bymonthday = recurrenceData.bymonthday || [];
  const bymonth = recurrenceData.bymonth || [];
  const timezone = recurrenceData.timezone || null;

  // Sets default value
  React.useEffect(() => {
    if (!newEvent.recurrenceData) {
      updateRecurrenceData((prev) => ({
        startDate: moment(),
        endDate: null,
        startTime: moment().hour(12).minute(0).second(0),
        endTime: moment().hour(14).minute(0).second(0),
        freq: 'DAILY',
        interval: 1,
        byweekday: [],
        bymonthday: [],
        bymonth: [],
        timezone: momentTZ.tz.guess(),
      }));
    }
  }, [newEvent.recurrence]);

  const handleRecurrenceChange = (e) => {
    const {name, value} = e;
    updateRecurrenceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleDaySelection = (day) => {
    updateRecurrenceData((prev) => {
      const {byweekday} = prev;
      return {
        ...prev,
        byweekday: byweekday.includes(day) ?
            byweekday.filter((d) => d !== day) : // Remove if already selected
            [...byweekday, day], // Add if not selected
      };
    });
  };

  const makeTimezonesList = React.useCallback(() => {
    const timezones = momentTZ.tz.names();
    const formatted = timezones.map((timezone) => {
      return {
        group: translate('client.ClientDetail.Scheduling.Timezone.Timezones'),
        value: timezone,
        label: timezone,
      };
    });
    return [...formatted];
  }, []);

  // Sets validation
  React.useEffect(() => {
    const isGenerallyValid = startDate && startTime && endTime && freq && interval && timezone;
    if (!isGenerallyValid) {
      setIsStepValid(false);
      return;
    }
    if (endTime) {
      if (moment(endDate).isBefore(moment(startTime))) {
        setIsStepValid(false);
        return;
      }
    }
    if (freq === 'WEEKLY') {
      if (!byweekday.length) {
        setIsStepValid(false);
        return;
      }
    }

    if (freq === 'MONTHLY') {
      if (!bymonthday.length) {
        setIsStepValid(false);
        return;
      }
      for (const monthDay of bymonthday) {
        if (monthDay < 1 || monthDay > 31) {
          setIsStepValid(false);
          return;
        }
      }
    }

    if (freq === 'YEARLY') {
      if (!(bymonthday.length && bymonth.length)) {
        setIsStepValid(false);
        return;
      }
      for (const monthDay of bymonthday) {
        if (monthDay < 1 || monthDay > 31) {
          setIsStepValid(false);
          return;
        }
      }
      for (const month of bymonth) {
        if (month < 1 || month > 12) {
          setIsStepValid(false);
          return;
        }
      }
    }

    setIsStepValid(true);
  }, [recurrenceData]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <DatePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.StartDate')}
          value={startDate}
          onChange={(date) => handleRecurrenceChange({name: 'startDate', value: date})}
          textFieldProps={{
            fullWidth: true,
            InputLabelProps: {shrink: true},
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <DatePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.EndDateDisplay')}
          value={endDate}
          onChange={(date) => handleRecurrenceChange({name: 'endDate', value: date})}
          textFieldProps={{
            fullWidth: true,
            InputLabelProps: {shrink: true},
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <Dropdown
          value={freq}
          onChange={(e) => handleRecurrenceChange({name: 'freq', value: e})}
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.Frequency')}
        >
          {
            frequencies.map((freqKey) => {
              return (
                <DropdownMenuItem key={freqKey} value={freqKey}>
                  {translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.FrequencyData.${freqKey}`)}
                </DropdownMenuItem>
              );
            })
          }
        </Dropdown>
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.Interval')}
          name="interval"
          type="number"
          value={interval}
          onChange={(e) => handleRecurrenceChange({name: 'interval', value: e})}
        />
      </Grid>
      <Grid item xs={6}>
        <TimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.StartTime')}
          value={startTime}
          onChange={(time) => handleRecurrenceChange({name: 'startTime', value: time})}
          textFieldProps={{
            fullWidth: true,
            InputLabelProps: {shrink: true},
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TimePicker
          label={translate('client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.EndTime')}
          value={endTime}
          onChange={(time) => handleRecurrenceChange({name: 'endTime', value: time})}
          textFieldProps={{
            fullWidth: true,
            InputLabelProps: {shrink: true},
          }}
        />
      </Grid>
      {/* Additional fields for WEEKLY, MONTHLY, YEARLY recurrence (optional based on freq) */}
      {freq === 'WEEKLY' && (
        <Grid item xs={12}>
          <Box display="flex">
            {
              daysOfWeek.map((day) => (
                <Button
                  key={day}
                  variant={byweekday.includes(day) ? 'contained' : 'outlined'}
                  onClick={() => toggleDaySelection(day)}
                  sx={{
                    minWidth: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    fontSize: '16px',
                    padding: '0',
                    mr: 1,
                  }}
                >
                  {translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.DayOfTheWeek.${day}`)}
                </Button>
              ))
            }
          </Box>
        </Grid>
      )}
      {freq === 'MONTHLY' && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonthDay`)}
            name="bymonthday"
            value={bymonthday.join(',')}
            onChange={(e) =>
              handleRecurrenceChange({
                name: 'bymonthday',
                value: e ? e.split(',').map(Number) : null,
              })
            }
          />
        </Grid>
      )}

      {freq === 'YEARLY' && (
        <>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonth`)}
              name="bymonth"
              value={bymonth.join(',')}
              onChange={(e) =>
                handleRecurrenceChange({
                  name: 'bymonth',
                  value: e ? e.split(',').map(Number) : null,
                })
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label={translate(`client.ClientDetail.Scheduling.ScheduleDetail.EventDate.RecurrenceEvent.ByMonthDay`)}
              name="bymonthday"
              value={bymonthday.join(',')}
              onChange={(e) =>
                handleRecurrenceChange({
                  name: 'bymonthday',
                  value: e ? e.split(',').map(Number) : null,
                })
              }
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Autocomplete
          options={makeTimezonesList()}
          value={timezone || momentTZ.tz.guess()}
          label={translate('client.ClientDetail.Scheduling.Timezone.Timezones')}
          onChange={(data) => handleRecurrenceChange({name: 'timezone', value: data.value})}
          sx={{minWidth: {xs: '192px', md: '256px'}}}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            );
          }}
          groupBy={(dropdownItem) => dropdownItem.group}
          isOptionEqualToValue={(option, value) => option.value === value}
          disableClearable
        />
      </Grid>
    </Grid>
  );
};

const EventDate = (props) => {
  const eventType = props.eventType;

  if (eventType === 'FIXED_EVENT') {
    return (
      <Fixed
        {...props}
      />
    );
  } else if (eventType === 'RECURRENCE_EVENT') {
    return (
      <Recurrence
        {...props}
      />
    );
  }
  return null;
};

Fixed.propTypes =
Recurrence.propTypes =
EventDate.propTypes = {
  eventType: PropTypes.oneOf(['RECURRENCE_EVENT', 'FIXED_EVENT']),
  setEventType: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
});
export default connect(mapStateToProps, dispatch)(React.memo(EventDate));
